import { Typography } from '@workspace/ui/components/Typography';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { IconButton } from '~/components/isolated/IconButton';
import { useLang } from '~/hooks/useLang';
import { userStore } from '~/stores';

import { LanguageList } from './LanguageList';

interface LanguageBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const LanguageBottomSheet: React.FC<LanguageBottomSheetProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { setLanguage } = useLang();

  const currentLang = userStore.lang || company.config.lang;

  const handleSelect = (lang: string) => {
    if (lang === currentLang) {
      onClose();
      return;
    }
    setLanguage(lang);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        role="presentation"
      />

      <div className="relative w-full max-w-lg rounded-t-6.5 bg-bg-base">
        <div className="flex items-center py-4 pr-4 pl-6">
          <Typography.Header150Medium className="flex-1 py-1 text-text-primary">
            {t('chooseLanguage')}
          </Typography.Header150Medium>
          <IconButton
            size="S"
            variant="outline"
            icon="close"
            onClick={onClose}
            aria-label={t('close')}
          />
        </div>

        <LanguageList
          currentLang={currentLang}
          onSelect={handleSelect}
          className="px-6 pt-2 pb-safe-offset-6"
        />
      </div>
    </div>,
    document.body,
  );
};

export default observer(LanguageBottomSheet);
